/* istanbul ignore file */
import { Cookie } from '@vue-util/cookie';
import { ref, onMounted, isRef } from 'vue';
import { useStore } from 'vuex';
import { useThrottleFn } from '@vueuse/core';
import $http from '@vue-util/http';
import isBcom from '../utils/isBcom';

let userGuid = null;
const getUserGuid = () => userGuid || (isBcom ? Cookie.get('bloomingdales_online_guid') : Cookie.get('macys_online_guid'));

async function fetchWishlist(loaded, error) {
  // if guid is missing, no need to load wishlist data
  const uGuid = getUserGuid();
  if (!uGuid) {
    return;
  }
  const store = useStore();
  const path = '/xapi/wishlist/v1/';
  const url = isBcom ? `${path}lists/?userGuid=${uGuid}&expand=items,userInfo` : `${path}${uGuid}/favorites?_=${Date.now()}`;
  store.dispatch('wishlistStore/fetchWishlist', url)
    .then(() => {
      loaded.value = true;
    })
    .catch((e) => {
      error.value = e;
    });
}

async function addToWishlist(id, isInWishlist, response, store) {
  const path = '/xapi/wishlist/v1/favorites';
  const url = `${path}`;
  const payload = {
    CustomerList: {
      list: [{
        items: [{
          qtyRequested: 1,
          product: {
            id: `${id}`,
          },
        }],
      }],
    },
  };
  const uGuid = getUserGuid();

  if (uGuid && uGuid !== '') {
    payload.CustomerList.user = {
      guid: uGuid,
    };
  }

  store.dispatch('wishlistStore/addToWishlist', { url, payload, id }).then(() => {
    isInWishlist.value = true;
    if (isRef(response)) {
      response.value = 'Added to list.';
    }
  }).catch(() => {
    if (isRef(response)) {
      response.value = 'Sorry, try later.';
    }
  });
}

async function removeFromWishlist(id, isInWishlist, response, store) {
  const uGuid = getUserGuid();
  if (uGuid === null) {
    response.value = 'Please login to try.';
    return;
  }

  const makeRemoveItemStoreAction = (url) => {
    store.dispatch('wishlistStore/removeFromWishlist', { url, id }).then(() => {
      isInWishlist.value = false;
      if (isRef(response)) {
        response.value = 'Removed from list.';
      }
    }).catch(() => {
      if (isRef(response)) {
        response.value = 'Sorry, try later.';
      }
    });
  };

  if (isBcom) {
    const wishlistXapi = '/xapi/wishlist/v1/lists';
    const url = `${wishlistXapi}?userGuid=${uGuid}&expand=items`;
    $http.get(url)
      .then((getResponse) => {
        const { items, listGuid } = getResponse.data.CustomerList.list.find((list) => list.defaultList);
        const itemsToRemove = items.filter((item) => Number.parseInt(item.upc.product.id, 10) === Number.parseInt(id, 10));
        const itemGuids = itemsToRemove.map((item) => item.itemGuid).join(',');
        const itemRemoveUrl = `${wishlistXapi}/${listGuid}/items/${itemGuids}`;

        makeRemoveItemStoreAction(itemRemoveUrl);
      })
      .catch(
        (error) => console.log('error getting wishlist data', error), // eslint-disable-line no-console
      );
  } else {
    const { listGuid } = store.state.wishlistStore;
    const path = '/xapi/wishlist/v1/favorites';
    const url = `${path}/${listGuid}?productId=${id}`;

    makeRemoveItemStoreAction(url);
  }
}

// used by consumer
export function useWishlistLoader(uGuid) {
  const loaded = ref(false);
  const error = ref(null);
  // make a global copy
  userGuid = uGuid;

  onMounted(() => {
    if (!loaded.value) {
      fetchWishlist(loaded, error);
    }
  });
  return { loaded, error };
}

// used by wishlist icon component
export function useWishlistManager(id) {
  const store = useStore();
  const isInWishlist = ref(false);
  const xapiRespMsg = ref('');
  const openWishListDrawer = () => { store.commit('slideoutStore/TOGGLE', 'wishlistDrawer'); };

  isInWishlist.value = store.state.wishlistStore.products.includes(id);

  const onClick = useThrottleFn(() => {
    if (isInWishlist.value) {
      removeFromWishlist(id, isInWishlist, xapiRespMsg, store);
    } else if (isBcom) {
      openWishListDrawer();
    } else {
      addToWishlist(id, isInWishlist, xapiRespMsg, store);
    }
  }, 500);

  return { isInWishlist, xapiRespMsg, onClick };
}
